import React, {useState, useEffect} from 'react';
import {Hub} from 'aws-amplify';
import {useDispatch} from 'react-redux';
import {BrowserRouter as Router, Switch, Route, Redirect} from 'react-router-dom';

import Login from '../auth/components/Login';
import ForgotPassword from '../auth/components/ForgotPassword';
import Register from '../auth/components/Register';
import ConfirmUser from '../auth/components/ConfirmUser';
import Home from '../home/components/Home';
import LessonViewer from './LessonViewer';
import ExerciseViewer from './ExerciseViewer';
import AssignmentViewer from './AssignmentViewer';
import LessonBuilder from '../lesson-builder/components/LessonBuilder';
import PrintableLessonWorksheet from '../lesson-builder/components/PrintableLessonWorksheet';
import CreateSession from '../lesson-builder/components/CreateSession';
import Session from '../lesson-builder/components/Session';
import StudentDashboard from './StudentDashboard';
import StudentLogin from './StudentLogin';
import Student from '../lesson-builder/components/Student';
import Students from '../lesson-builder/components/Students';
import Lessons from '../lesson-builder/components/Lessons';
import Lesson from '../lesson-builder/components/Lesson';
import Exercise from '../lesson-builder/components/Exercise';
import Exercises from '../lesson-builder/components/Exercises';
import ChangePassword from '../lesson-builder/components/ChangePassword';
import ProfessionalInformation from '../lesson-builder/components/ProfessionalInformation';
import Profile from '../lesson-builder/components/Profile';

import AuthService from '../auth/services/AuthService';
import AuthLoading from '../auth/components/AuthLoading';
import {fetchUser as fetchAuthUser} from '../redux/slices/auth';
import {fetchUser, fetchUserGroup} from '../redux/slices/user';

export default function AppRouter() {
  const dispatch = useDispatch();
  const [isLoggedIn, setLoggedIn] = useState(null);
  const [isInitialDataLoaded, toggleInitialDataLoaded] = useState(false);

  // load initial data into redux on login
  useEffect(() => {
    if (isLoggedIn) {
      Promise.all([
        dispatch(fetchAuthUser()),
        dispatch(fetchUser()).then((user) => {
          return dispatch(fetchUserGroup(user.userGroupID));
        }),
      ]).then(() => toggleInitialDataLoaded(true));
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    const listener = ({payload: {event, data}}) => {
      console.log(`AUTH EVENT: ${event}`);

      if (event === 'signIn') {
        setLoggedIn(true);
      } else if (event === 'signOut') {
        setLoggedIn(false);
        toggleInitialDataLoaded(false);
      }
    };

    const checkAuth = async () => {
      const {isAuthenticated} = await AuthService.checkAuthentication();
      setLoggedIn(isAuthenticated);
    };
    checkAuth();

    const hubListenerCancelToken = Hub.listen('auth', listener);
    return function cleanup() {
      return hubListenerCancelToken;
    };
  });

  function PrivateRoute({children, ...rest}) {
    return (
      <Route
        {...rest}
        render={({location}) =>
          isLoggedIn ? (
            children
          ) : (
            <Redirect
              to={{
                pathname: '/login',
                state: {from: location},
              }}
            />
          )
        }
      />
    );
  }

  if (isLoggedIn === null || (isLoggedIn && !isInitialDataLoaded)) return <AuthLoading />;

  return (
    <Router>
      <Switch>
        <Route path="/login">
          <Login />
        </Route>
        <Route path="/forgot-password">
          <ForgotPassword />
        </Route>
        <Route path="/register">
          <Register />
        </Route>
        <Route path="/confirm">
          <ConfirmUser />
        </Route>

        <PrivateRoute path="/builder/session/create">
          <CreateSession />
        </PrivateRoute>
        <PrivateRoute path="/builder/session/:sessionID">
          <Session />
        </PrivateRoute>
        <PrivateRoute path="/builder/student/create">
          <Student />
        </PrivateRoute>
        <PrivateRoute path="/builder/student/:studentID?">
          <Student />
        </PrivateRoute>
        <PrivateRoute path="/builder/students">
          <Students />
        </PrivateRoute>
        <PrivateRoute path="/builder/lessons/:lessonID/worksheet">
          <PrintableLessonWorksheet />
        </PrivateRoute>
        <PrivateRoute path="/builder/lessons/:lessonID">
          <Lesson />
        </PrivateRoute>
        <PrivateRoute path="/builder/lessons">
          <Lessons />
        </PrivateRoute>
        <PrivateRoute path="/builder/exercise/:exerciseID?">
          <Exercise />
        </PrivateRoute>
        <PrivateRoute path="/builder/exercises">
          <Exercises />
        </PrivateRoute>
        <PrivateRoute path="/builder/professional-info">
          <ProfessionalInformation />
        </PrivateRoute>
        <PrivateRoute path="/builder/change-password">
          <ChangePassword />
        </PrivateRoute>
        <PrivateRoute path="/builder/profile">
          <Profile />
        </PrivateRoute>
        <PrivateRoute path="/builder">
          <LessonBuilder />
        </PrivateRoute>

        <Route path="/student/dashboard">
          <StudentDashboard />
        </Route>
        <Route path="/student/login">
          <StudentLogin />
        </Route>
        <Route path="/lesson/:lessonID">
          <LessonViewer />
        </Route>
        <Route path="/exercise/:exerciseID">
          <ExerciseViewer />
        </Route>
        <Route path="/assignment">
          <AssignmentViewer />
        </Route>

        <Route exact path="/">
          <Home />
        </Route>
      </Switch>
    </Router>
  );
}
